// Shown in place of a card's entry count before the live thematic_tags fetch resolves,
// when it fails, or when it resolves without this category's tag. DEFAULT_CATEGORIES uses
// this as entriesCount's own shipped value (not a real number) so there is no state — SSR,
// first client paint, or a failed/pending fetch — in which a stale hardcoded count can be
// shown; the only values ever visible are this placeholder or a real live count.
export const THEMATIC_TAG_COUNT_PLACEHOLDER = '--';

export interface PortalCategory {
  title: string;
  // String, not number: the live value comes from the thematic_tags facet API
  // (see BrowseDataCarouselComponent) and falls back to THEMATIC_TAG_COUNT_PLACEHOLDER when
  // that call fails or omits this card's tag — a plain number field can't hold that.
  entriesCount: string;
  description: string;
  // Longer description shown in the search-results page banner when a user
  // arrives via this card — separate from `description` (the card's own,
  // shorter blurb) since Figma specifies different copy for each.
  searchResultsDescription: string;
  accentColor: string;
  categorySlug: string;
  // The value of the backend's `thematic_tags` field for this category. A card
  // search navigates to the readable /search/thematic-tag/<categorySlug> browser
  // URL (see PortalCardComponent); SearchComponent normalizes the 'thematic-tag'
  // route segment back to the backend's actual 'thematic_tags' field name, so the
  // existing generic field-search mechanism (see cleanParamValue) still turns it
  // into q=thematic_tags:<thematicTag> — the same way organismScientificName,
  // providerId, etc. already work. Confirmed with backend (2026-08-06).
  thematicTag: string;
}

export const DEFAULT_CATEGORIES: PortalCategory[] = [
  {
    title: 'Pandemic Preparedness',
    entriesCount: THEMATIC_TAG_COUNT_PLACEHOLDER,
    description: 'Protein structure predictions from human and animal viruses with pandemic potential to support research on viral mechanisms, vaccines, and antiviral development.',
    searchResultsDescription: 'Includes protein structure predictions from human and animal viruses with pandemic potential or that have caused major outbreaks to support research into viral mechanisms, vaccine design, and antiviral development to strengthen global preparedness against outbreaks.',
    accentColor: '#00C247',
    categorySlug: 'pandemic-preparedness',
    thematicTag: 'PPP'
  },
  {
    title: 'Antimicrobial Resistance (AMR)',
    entriesCount: THEMATIC_TAG_COUNT_PLACEHOLDER,
    description: 'Bacterial proteins implicated in antimicrobial resistance, curated from published literature. Supports research into resistance mechanisms and new antimicrobial strategies.',
    searchResultsDescription: 'Bacterial proteins implicated in antimicrobial resistance, curated from published literature. Supports research into resistance mechanisms and new antimicrobial strategies.',
    accentColor: '#EA580C',
    categorySlug: 'antimicrobial-resistance',
    thematicTag: 'AMR'
  },
  {
    title: 'Neglected Tropical Diseases (NTDs)',
    entriesCount: THEMATIC_TAG_COUNT_PLACEHOLDER,
    description: 'Protein structure predictions from organisms causing neglected tropical diseases. Enables research into pathogen biology and drug or vaccine target discovery.',
    searchResultsDescription: 'Includes protein structure predictions from organisms responsible for neglected tropical diseases. This category enables research into organism biology and facilitates the discovery of drug and vaccine targets for these under-studied diseases.',
    accentColor: '#EC4899',
    categorySlug: 'neglected-tropical-diseases',
    thematicTag: 'NTD'
  },
  {
    title: 'WHO Global Health Proteomes',
    entriesCount: THEMATIC_TAG_COUNT_PLACEHOLDER,
    description: 'Protein structure predictions from the 30 WHO priority global health proteomes. Supports research on pathogens of major global public health importance.',
    searchResultsDescription: 'Includes protein structure predictions from the 30 World Health Organization (WHO) priority global health proteomes. This comprehensive collection supports coordinated international research efforts targeting the organisms of greatest public health significance worldwide.',
    accentColor: '#FFBD32',
    categorySlug: 'who-global-health-proteomes',
    thematicTag: 'WHO'
  }
];
